import { useState, useCallback, useEffect } from 'react'
import CardGrid from './CardGrid'
import CardScanner from './CardScanner'
import CardTimeline from './CardTimeline'
import CollectionFilters from './CollectionFilters'
import CollectionToolbar, { COLLECTION_SORTS, COLLECTION_VIEWS } from './CollectionToolbar'
import { getOwnerCardDisplayName } from '../utils/ownerCardDisplay'
import { compareCardTypeKeys, getCardTypeKey, getCardTypeLabel } from '../utils/cardTypes'

const VIEW_STORAGE_KEY = 'm1nt-owner-collection-view'
const SORT_STORAGE_KEY = 'm1nt-owner-collection-sort'
const FILTERS_STORAGE_KEY = 'm1nt-owner-collection-filters'

function readFiltersOpen() {
  try {
    const stored = window.localStorage.getItem(FILTERS_STORAGE_KEY)
    if (stored === 'open') return true
    if (stored === 'closed') return false
  } catch {
    // Fall through to the viewport default.
  }
  return window.matchMedia?.('(min-width: 1024px)').matches ?? false
}

const ALERT_DEFINITIONS = [
  { key: 'updates', label: 'Unread updates' },
  { key: 'custody', label: 'Custody pending' },
  { key: 'approval', label: 'Awaiting 2nd approval' },
]

function toggleInList(list, value) {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value]
}

function readStoredChoice(storageKey, allowed, fallback) {
  try {
    const stored = window.localStorage.getItem(storageKey)
    return allowed.some((option) => option.key === stored) ? stored : fallback
  } catch {
    return fallback
  }
}

function storeChoice(storageKey, value) {
  try {
    window.localStorage.setItem(storageKey, value)
  } catch {
    // Storage can be unavailable (private mode); the choice then lasts for this visit only.
  }
}

function activityTime(item) {
  return new Date(item?.order?.createdAt || item?.card?.createdAt || 0).getTime()
}

/** "recent" keeps the order given by the parent (in-process cards first, then latest activity). */
function sortItems(items, sort) {
  if (sort === 'oldest') return [...items].sort((a, b) => activityTime(a) - activityTime(b))
  if (sort === 'name-asc' || sort === 'name-desc') {
    const direction = sort === 'name-asc' ? 1 : -1
    return [...items].sort(
      (a, b) =>
        direction *
        getOwnerCardDisplayName(a.card).localeCompare(getOwnerCardDisplayName(b.card), undefined, {
          sensitivity: 'base',
        })
    )
  }
  return items
}

/**
 * Owner card list: grid → full-screen scanner → chain-of-custody timeline.
 * Does not replace navbar, stats, or filters (parent owns those).
 * @param {((item: { card: object, order: object }) => void) | undefined} directCardNavigation - If set (e.g. concluded filter), skip scanner/timeline and run this (e.g. navigate to card detail).
 * @param {(item: { card: object, order: object }) => boolean | undefined} onBeforeCardOpen - If returns true, default open flow is skipped (e.g. navigate to detail for burned cards).
 */
export default function CardJourney({
  items,
  cardImages,
  loadingImages,
  onOpenModal,
  onCustodyReview,
  onRequestLabCustodyClaim,
  ownerHasLinkedWallet = false,
  hasUnreadForOrder,
  directCardNavigation,
  autoOpenCardId = null,
  onAutoOpenConsumed,
  onBeforeCardOpen,
  statusOptions = [],
  statusFilter = 'all',
  onStatusChange,
}) {
  const [view, setView] = useState('grid')
  const [selected, setSelected] = useState(null)
  const [timelineData, setTimelineData] = useState([])
  const [query, setQuery] = useState('')
  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectedAlerts, setSelectedAlerts] = useState([])
  const [filtersOpen, setFiltersOpen] = useState(readFiltersOpen)
  const [sort, setSort] = useState(() =>
    readStoredChoice(SORT_STORAGE_KEY, COLLECTION_SORTS, 'recent')
  )
  const [viewMode, setViewMode] = useState(() =>
    readStoredChoice(VIEW_STORAGE_KEY, COLLECTION_VIEWS, 'grid')
  )

  const handleSortChange = useCallback((next) => {
    setSort(next)
    storeChoice(SORT_STORAGE_KEY, next)
  }, [])

  const handleToggleFilters = useCallback(() => {
    setFiltersOpen((open) => {
      storeChoice(FILTERS_STORAGE_KEY, open ? 'closed' : 'open')
      return !open
    })
  }, [])

  const handleViewModeChange = useCallback((next) => {
    setViewMode(next)
    storeChoice(VIEW_STORAGE_KEY, next)
  }, [])

  const handleCardClick = useCallback(
    (item) => {
      if (onBeforeCardOpen?.(item)) return
      if (directCardNavigation) {
        directCardNavigation(item)
        return
      }
      setSelected(item)
      setView('scanning')
    },
    [directCardNavigation, onBeforeCardOpen]
  )

  const handleScanComplete = useCallback((data) => {
    setTimelineData(Array.isArray(data) ? data : [])
    setView('timeline')
  }, [])

  const handleScanCancel = useCallback(() => {
    setView('grid')
    setSelected(null)
    setTimelineData([])
  }, [])

  const handleBack = useCallback(() => {
    setView('grid')
    setSelected(null)
    setTimelineData([])
  }, [])

  const handleOpenDetails = useCallback(() => {
    if (selected && onOpenModal) {
      onOpenModal(selected)
    }
  }, [selected, onOpenModal])

  useEffect(() => {
    if (!autoOpenCardId || directCardNavigation || !items?.length) return
    const item = items.find((it) => it.card?.id === autoOpenCardId)
    if (!item) return
    if (item.card?.status === 'BURNED') {
      onAutoOpenConsumed?.()
      return
    }
    onAutoOpenConsumed?.()
    setSelected(item)
    setTimelineData([])
    setView('scanning')
  }, [autoOpenCardId, items, directCardNavigation, onAutoOpenConsumed])

  /** If the open card leaves the filtered list after a refresh, return to the grid. */
  useEffect(() => {
    if (view === 'grid' || !selected?.card?.id) return
    const stillHere = items.some((it) => it.card?.id === selected.card.id)
    if (!stillHere) {
      setView('grid')
      setSelected(null)
      setTimelineData([])
    }
  }, [items, view, selected?.card?.id])

  if (!items?.length) return null

  const needle = query.trim().toLowerCase()
  const searchedItems = needle
    ? items.filter((item) =>
        `${getOwnerCardDisplayName(item.card)} ${item.card?.cardType || ''}`
          .toLowerCase()
          .includes(needle)
      )
    : items

  const itemMatchesAlert = (item, alertKey) => {
    if (alertKey === 'updates') return Boolean(hasUnreadForOrder?.(item.order?.id, item.card?.id))
    if (alertKey === 'custody')
      return Boolean(item.custodyRequest && !item.custodyRequest.isExpired)
    if (alertKey === 'approval') {
      return Boolean(item.burnPendingAwaitingSecondApproval) && item.card?.status !== 'BURNED'
    }
    return false
  }

  // Options list every category in this status view; counts follow the active search.
  const typeKeys = [...new Set(items.map((item) => getCardTypeKey(item.card?.cardType)))].sort(
    compareCardTypeKeys
  )
  const activeTypes = selectedTypes.filter((key) => typeKeys.includes(key))
  const typeOptions = typeKeys.map((key) => ({
    key,
    label: getCardTypeLabel(key),
    count: searchedItems.filter((item) => getCardTypeKey(item.card?.cardType) === key).length,
  }))
  const alertOptions = ALERT_DEFINITIONS.map((definition) => ({
    ...definition,
    count: searchedItems.filter((item) => itemMatchesAlert(item, definition.key)).length,
  })).filter((option) => option.count > 0 || selectedAlerts.includes(option.key))
  const activeAlerts = selectedAlerts.filter((key) =>
    alertOptions.some((option) => option.key === key)
  )

  const typedItems = activeTypes.length
    ? searchedItems.filter((item) => activeTypes.includes(getCardTypeKey(item.card?.cardType)))
    : searchedItems
  const filteredItems = activeAlerts.length
    ? typedItems.filter((item) => activeAlerts.some((key) => itemMatchesAlert(item, key)))
    : typedItems
  const visibleItems = sortItems(filteredItems, sort)

  const activeFilterCount = activeTypes.length + activeAlerts.length
  const canClearFilters = activeFilterCount > 0 || Boolean(needle)
  const clearFilters = () => {
    setQuery('')
    setSelectedTypes([])
    setSelectedAlerts([])
  }
  const removableFilters = [
    ...activeTypes.map((key) => ({
      id: `type:${key}`,
      label: getCardTypeLabel(key),
      remove: () => setSelectedTypes((list) => list.filter((entry) => entry !== key)),
    })),
    ...activeAlerts.map((key) => ({
      id: `alert:${key}`,
      label: ALERT_DEFINITIONS.find((definition) => definition.key === key)?.label ?? key,
      remove: () => setSelectedAlerts((list) => list.filter((entry) => entry !== key)),
    })),
  ]

  const imageUrl = selected ? cardImages[selected.card?.id]?.frontImageUrl : null

  return (
    <div className="relative min-h-[200px]">
      {view === 'grid' && (
        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-8">
          {filtersOpen && (
            <aside
              aria-label="Collection filters"
              className="mb-6 w-full rounded-[8px] border border-mk-line p-4 lg:sticky lg:top-24 lg:mb-0 lg:max-h-[calc(100vh-7rem)] lg:w-[260px] lg:shrink-0 lg:overflow-y-auto lg:rounded-none lg:border-0 lg:border-r lg:p-0 lg:pr-6"
            >
              <CollectionFilters
                statusOptions={statusOptions}
                statusFilter={statusFilter}
                onStatusChange={onStatusChange}
                typeOptions={typeOptions}
                selectedTypes={activeTypes}
                onToggleType={(key) => setSelectedTypes((list) => toggleInList(list, key))}
                alertOptions={alertOptions}
                selectedAlerts={activeAlerts}
                onToggleAlert={(key) => setSelectedAlerts((list) => toggleInList(list, key))}
                canClear={canClearFilters}
                onClear={clearFilters}
              />
            </aside>
          )}

          <div className="min-w-0 flex-1">
            <CollectionToolbar
              query={query}
              onQueryChange={setQuery}
              sort={sort}
              onSortChange={handleSortChange}
              view={viewMode}
              onViewChange={handleViewModeChange}
              filtersOpen={filtersOpen}
              onToggleFilters={handleToggleFilters}
              activeFilterCount={activeFilterCount}
              itemCount={visibleItems.length}
            />

            {removableFilters.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {removableFilters.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={filter.remove}
                    aria-label={`Remove filter ${filter.label}`}
                    className="group flex min-h-[32px] items-center gap-2 border border-m1-line px-2.5 text-[13px] text-m1-ink-soft transition-colors hover:border-m1-violet hover:text-m1-ink focus:outline-none focus-visible:border-m1-violet"
                  >
                    {filter.label}
                    <i
                      className="ri-close-line text-base text-m1-muted group-hover:text-m1-violet"
                      aria-hidden="true"
                    />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={clearFilters}
                  className="min-h-[32px] px-1 text-[13px] text-m1-muted underline-offset-4 transition-colors hover:text-m1-violet hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}

            <div className="mt-5">
              {visibleItems.length > 0 ? (
                <CardGrid
                  items={visibleItems}
                  view={viewMode}
                  cardImages={cardImages}
                  loadingImages={loadingImages}
                  onCardClick={handleCardClick}
                  onCustodyReview={onCustodyReview}
                  onRequestLabCustodyClaim={onRequestLabCustodyClaim}
                  ownerHasLinkedWallet={ownerHasLinkedWallet}
                  hasUnreadForOrder={hasUnreadForOrder}
                />
              ) : (
                <div className="border border-m1-line px-6 py-14 text-center">
                  <h3 className="font-m1-display text-[28px] font-normal text-m1-ink">
                    No matching cards
                  </h3>
                  <p className="mx-auto mt-3 max-w-[28em] text-[16px] font-light leading-relaxed text-m1-muted">
                    Nothing in this view matches your search and filters.
                  </p>
                  {canClearFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-6 min-h-[44px] border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {view === 'scanning' && selected && (
        <CardScanner
          isOpen
          card={selected.card}
          order={selected.order}
          imageUrl={imageUrl}
          onComplete={handleScanComplete}
          onCancel={handleScanCancel}
        />
      )}

      {view === 'timeline' && selected && (
        <CardTimeline
          card={selected.card}
          order={selected.order}
          steps={timelineData}
          imageUrl={imageUrl}
          onBack={handleBack}
          onOpenDetails={onOpenModal ? handleOpenDetails : undefined}
        />
      )}
    </div>
  )
}
